import styles from "./EventItem.module.scss";
import { ICalendarEventModel } from "../models/calendarEventModel";
import { classNames } from "primereact/utils";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { Tag } from "../../../../common/components/common/Tag/Tag";
import { UserEventStatus } from "../../../../common/enums";
import { getAvailableSpacesLabel } from "../utils/portalCalendarUtility";
import { useAppContext } from "../../../../AppContext";
import { isReservationActive } from "../../../../common/infrastructure/utilities/reservationUtility";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import {
  formatDuration,
  getEventCalendarTimeLabel,
  isMultiDayDuration,
} from "../../../../common/infrastructure/utilities/durationUtility";
import { isMultisportAcceptedForEvent } from "../../../../common/utilities/paymentUtility";
import { MultisportTag } from "../../../../common/components/common/MultisportTag/MultisportTag";

interface IEventItemProps {
  className?: string;
  item?: ICalendarEventModel;
  isLoading: boolean;
  onEventClick: (id: number) => void;
  actionButtons?: React.ReactNode;
}
export const EventItem = ({
  className,
  item,
  isLoading,
  actionButtons,
  onEventClick,
}: IEventItemProps) => {
  const { selectedStudio } = useAppContext();

  const status = item?.userReservations?.some(
    (r) =>
      (r.status === UserEventStatus.Pending ||
        r.status === UserEventStatus.Confirmed) &&
      isReservationActive(r.isCancelled, r.status)
  )
    ? "ЗАПИСАН"
    : item?.userReservations?.some(
        (r) =>
          r.status === UserEventStatus.WaitingList &&
          isReservationActive(r.isCancelled, r.status)
      )
    ? "В ИЗЧАКВАЩИ"
    : null;

  const availableSpacesLabel =
    !item?.isReservationOpen && selectedStudio?.displayClosedReservationsLabel
      ? "Затворени резервации"
      : getAvailableSpacesLabel(item);

  const availableSpacesColor =
    item?.hasAvailableSpaces ||
    item?.userReservations.some((x) => x.status === UserEventStatus.Confirmed)
      ? "#7ff0cb"
      : "#a5cafe";

  const isCancelled = !!item?.isCancelled;

  const showMultisportTag =
    !isCancelled &&
    isMultisportAcceptedForEvent(
      item?.isMultisportAvailable,
      selectedStudio?.supportedPaymentTypes,
    );

  return (
    <div
      className={classNames(styles.root, className, {
        [styles.root_passed]: item?.isPassed && !isCancelled,
        [styles.root_cancelled]: isCancelled,
      })}
      onClick={() => (item?.id ? onEventClick(item?.id!) : () => {})}
    >
      <div className={styles.root_left_section}>
        <SkeletonItem
          isLoading={isLoading}
          value={
            <div className={styles.root_left_section_title}>
              <span
                className={classNames({
                  [styles.root_left_section_title_struck]: isCancelled,
                })}
              >
                {item?.name}
              </span>
              {isCancelled ? (
                <Tag
                  className={styles.root_left_section_title_tag}
                  backgroundColor="#ff8186"
                  label="Няма да се проведе"
                />
              ) : (
                <Tag
                  className={styles.root_left_section_title_tag}
                  backgroundColor={availableSpacesColor}
                  label={status ?? availableSpacesLabel}
                />
              )}
              <Tag
                className={styles.root_left_section_title_tag}
                label={getEventCalendarTimeLabel({
                  startTime: item?.startTime,
                  endTime: item?.endTime,
                  durationInMinutes: item?.durationInMinutes,
                })}
                tooltip={
                  isMultiDayDuration(item?.durationInMinutes)
                    ? `Започва в ${item?.startTime}, продължава ${formatDuration(
                        item?.durationInMinutes,
                      )}`
                    : undefined
                }
                backgroundColor="#f8bca3"
              />
              <RenderWhen condition={!!item?.instructor?.trim()}>
                <Tag
                  className={styles.root_left_section_title_tag}
                  label={item?.instructor ?? ""}
                  backgroundColor="#a8d1fc"
                />
              </RenderWhen>
              <RenderWhen condition={showMultisportTag}>
                <MultisportTag className={styles.root_left_section_title_tag} />
              </RenderWhen>
            </div>
          }
        />
      </div>
      <div className={styles.root_right_section}>
        {actionButtons}
        <Icon type={IconType.RightChevronIcon} />
      </div>
    </div>
  );
};
